import React from "react";
import { Link, useParams } from "react-router-dom";
import SiteShell from "../../components/layout/SiteShell";
import ContentPageLayout from "../../components/content/ContentPageLayout";
import SEO from "../../components/seo/SEO";
import WellnessDisclaimer from "../../components/content/WellnessDisclaimer";
import ConversionCtas from "../../components/content/ConversionCtas";
import { getBlendBySlug } from "../../lib/blends";
import { getIngredientById } from "../../lib/ingredients";

export default function BlendDetailPage() {
  const { slug } = useParams();
  const blend = slug ? getBlendBySlug(slug) : null;

  if (!blend) {
    return (
      <SiteShell>
        <SEO title="Blend routine not found" description="This MossMatrix blend page could not be found." path="/blends" />
        <ContentPageLayout eyebrow="Blends" title="Not found" subtitle="That blend page doesn’t exist.">
          <Link to="/blends" className="underline text-forest">
            All blend routines
          </Link>
        </ContentPageLayout>
      </SiteShell>
    );
  }

  const path = `/blends/${blend.slug}`;
  const basesLabel =
    blend.preferredBases.length === 2
      ? "Gold or Purple sea moss base"
      : blend.preferredBases[0] === "purple"
        ? "Purple sea moss base"
        : "Gold sea moss base";

  return (
    <SiteShell>
      <SEO title={blend.seoTitle} description={blend.seoDescription} path={path} ogType="article" />
      <ContentPageLayout eyebrow="Blend routine" title={blend.name} subtitle={blend.headline}>
        <section className="rounded-3xl border border-cream bg-white p-8 mb-10 max-w-3xl">
          <h2 className="font-serif text-xl text-forest">How we talk about this blend</h2>
          <p className="mt-3 text-sm text-botanical/85 leading-relaxed">{blend.routineCopy}</p>
          <p className="mt-4 text-xs text-botanical/65">
            <strong className="text-forest">Suggested base:</strong> {basesLabel}.{" "}
            <strong className="text-forest">Herbs in the story:</strong>{" "}
            {blend.ingredientIds
              .map((id) => getIngredientById(id)?.name || id)
              .filter(Boolean)
              .join(" · ")}
            .
          </p>
        </section>

        <section className="mb-12 max-w-3xl">
          <h2 className="font-serif text-xl text-forest mb-4">Ingredients in this routine</h2>
          <ul className="space-y-3">
            {blend.ingredientIds.map((id) => {
              const row = getIngredientById(id);
              if (!row) return null;
              return (
                <li key={id}>
                  <Link
                    to={`/ingredients/${row.slug}`}
                    className="flex items-center justify-between gap-4 rounded-2xl border border-cream bg-offwhite px-4 py-3 hover:border-gold/50 transition"
                  >
                    <span className="font-serif text-forest">{row.name}</span>
                    <span className="text-[11px] tracking-widest uppercase text-botanical/60">Library →</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>

        <WellnessDisclaimer className="max-w-3xl mb-10" />

        <div className="flex flex-wrap gap-3 mb-12">
          <Link to="/shop" className="rounded-full bg-forest text-cream px-6 py-3 text-sm hover:bg-botanical transition">
            Shop plain sea moss
          </Link>
          <Link to="/mix-ins" className="rounded-full border border-forest/20 px-6 py-3 text-sm hover:bg-cream transition">
            Explore mix-in ideas
          </Link>
        </div>

        <ConversionCtas />
        <p className="text-center text-sm text-botanical/70 mt-10">
          <Link to="/blends" className="underline hover:text-forest">
            All blend routines
          </Link>
        </p>
      </ContentPageLayout>
    </SiteShell>
  );
}
